import { Amplify } from 'aws-amplify';
import {
    signIn,
    signOut,
    getCurrentUser,
    fetchAuthSession,
    signInWithRedirect,
    resetPassword,
    fetchUserAttributes,
    confirmResetPassword,
    confirmSignIn
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import * as AmazonCognitoIdentity from "amazon-cognito-identity-js";

import UserTokenManagementService from "./userTokenManagementService";
import logger from "./loggerService";

import defaultConfigForCognito from "../constants/cognitoConfig";
import {environment} from "../environments/environment";

const domain = '.guideline.ai';
const defaultSessionExpTime = 3600;

const listener = async (data) => {
    switch (data?.payload?.event) {
        case 'tokenRefresh':
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            const accessToken = session.tokens?.accessToken?.toString();
            // Note: In v6, refresh token is not directly accessible from fetchAuthSession
            if (idToken) {
                document.cookie = `idToken=${idToken}; domain=${domain}; max-age=${defaultSessionExpTime}; path=/; Secure; SameSite=Lax`;
            }
            if (accessToken) {
                document.cookie = `accessToken=${accessToken}; domain=${domain}; max-age=${defaultSessionExpTime}; path=/; Secure; SameSite=Lax`;
            }
            break;
        default:
            break;
    }
};

Hub.listen('auth', listener);

class CognitoService {
    _configuration;

    constructor(externalConfig) {
        const currentHost = window.location.origin;
        const currentHostWithoutGetParameters = window.location.protocol + "//" + window.location.host + window.location.pathname+'/'

        this.tokenManagement = new UserTokenManagementService();

        const defaultConfig = {
            loginUrl: defaultConfigForCognito.loginURL,
            identityPoolId: defaultConfigForCognito.identityPoolId,
            region: defaultConfigForCognito.region,
            userPoolId: defaultConfigForCognito.userPoolId,
            userPoolWebClientId: defaultConfigForCognito.userPoolWebClientId,
            oauth: {
                domain: defaultConfigForCognito.oauth.domain,
                redirectSignIn: `${currentHost}/access-token`,
                redirectSignOut: `${currentHostWithoutGetParameters}/`,
                scope: ['email', 'openid', 'aws.cognito.signin.user.admin', 'profile'],
                responseType: 'code'
            }
        }

        this._configuration = externalConfig || defaultConfig;

        // Amplify v6 configuration format
        Amplify.configure({
            Auth: {
                Cognito: {
                    userPoolId: this._configuration.userPoolId,
                    userPoolClientId: this._configuration.userPoolWebClientId,
                    identityPoolId: this._configuration.identityPoolId,
                    loginWith: {
                        oauth: {
                            domain: this._configuration.oauth.domain,
                            scopes: this._configuration.oauth.scope,
                            redirectSignIn: [this._configuration.oauth.redirectSignIn],
                            redirectSignOut: [this._configuration.oauth.redirectSignOut],
                            responseType: this._configuration.oauth.responseType
                        }
                    }
                }
            }
        });
    }

    /**
     * Clean the browser history to prevent the user from navigating back after logout
     */
    clearBrowserHistory() {
        try {
            if (window.history && window.history.replaceState) {
                // Replace the current history entry with the current URL
                window.history.replaceState(null, null, window.location.href);
                
                // If there are multiple entries in the history, try to clean them
                if (window.history.length > 1) {
                    // Create a new entry to replace the entire previous history
                    window.history.pushState(null, null, window.location.href);
                    window.history.replaceState(null, null, window.location.href);
                }
            }
        } catch (error) {
            console.warn('Browser history error:', error);
        }
    }

    /**
     * Clean all local storage data from the browser
     */
    clearLocalStorage() {
        try {
            localStorage.clear();
            sessionStorage.clear();
        } catch (error) {
            console.warn('Local storage error:', error);
        }
    }
  
    /**
     * Complete cleanup function for logout that combines history, storage and cookies cleanup
     */
    performLogoutCleanup() {
        this.clearBrowserHistory();
        this.clearLocalStorage();
        this.clearCookies();
    }

    /**
     * Prevent back navigation by adding a listener to the popstate event
     */
    preventBackNavigation() {
        // Add an entry to the history
        window.history.pushState(null, null, window.location.href);
        
        // Listener to prevent back navigation
        const preventBack = (event) => {
            window.history.pushState(null, null, window.location.href);
        };
        
        window.addEventListener('popstate', preventBack);
        
        // Return function to remove the listener if needed
        return () => {
            window.removeEventListener('popstate', preventBack);
        };
    }

    async signInUser(userName, password, rememberMe) {
        const result = await signIn({ username: userName, password: password });
        if (result.isSignedIn) {
            logger.info('user successfully signed in', userName);
        }
        window.localStorage.setItem('rememberMe', rememberMe);
        return result;
    }

    async federatedSignIn(provider) {
        return await signInWithRedirect({ 
            provider: { custom: provider },
            customState: new Date().getTime().toString()
        });
    }

    async signInUserWithSSO() {
        try {
            return signInWithRedirect({ provider: 'Auth0' });
        } catch (e) {
            console.log(e);
        }
    }


    async signOutCurrentSession() {
        try {
            await signOut({ global: false }); // current session only — preserves other active sessions
        } catch (e) {
            console.error('Error during local signOut:', e);
        } finally {
            this.performLogoutCleanup();
        }
    }

    async signOutUser(returnUrl = '') {
        try {
            // Use the internal cleanup methods
            this.performLogoutCleanup();

            // Persist returnUrl across the federated logout round-trip. Must run synchronously
            // AFTER the cleanup above and BEFORE signOut(): for hosted-UI (SSO) sessions
            // Amplify v6 itself navigates the browser to the Cognito /logout endpoint inside
            // signOut(), so any code after the await races the page unload and is
            // intermittently skipped (MT-23471).
            if (returnUrl) {
                localStorage.setItem('logoutReturnUrl', returnUrl);
            }

            // Perform the Cognito signOut with global flag to invalidate all sessions
            await signOut({ global: true });

            // Defense-in-depth re-clear: wipe anything written to localStorage during
            // signOut() while preserving logoutReturnUrl. As of aws-amplify v6 the
            // sign-out path only clears storage (completeOAuthSignOut -> clearOAuthData /
            // clearTokens / clearCredentials), but a future upgrade or a Hub listener
            // could write here. NOTE: best-effort only — for hosted-UI (SSO) sessions
            // Amplify navigates away inside signOut(), so this block may never run; the
            // cleanup that MUST happen is performLogoutCleanup() before signOut(). This
            // block is synchronous, so it either runs atomically or not at all — it
            // cannot drop logoutReturnUrl midway (MT-23471).
            const preservedReturnUrl = localStorage.getItem('logoutReturnUrl');
            localStorage.clear();
            if (preservedReturnUrl) {
                localStorage.setItem('logoutReturnUrl', preservedReturnUrl);
            }

            // HACK to enforce signeOut from the federated provider we have to call this endpoint
            // https://github.com/aws-amplify/amplify-js/issues/1583#issuecomment-490315706
            const logoutReturnUrl = this._configuration.loginUrl + '/'

            const federatedSigneOutUrl = `https://${this._configuration.oauth.domain}/logout?client_id=${this._configuration.userPoolWebClientId}&logout_uri=${logoutReturnUrl}`;
            // Fallback redirect for non-hosted-UI sessions — Amplify only redirects on its own
            // when OAuth sign-in state exists. Use replace to avoid going back.
            window.location.replace(federatedSigneOutUrl);

        } catch (e) {
            console.log('Error during signOut:', e);
            // Even in case of error, still clean local data and redirect
            this.performLogoutCleanup();

            // Re-persist the returnUrl after cleanup so the error path does not lose it (MT-23471)
            if (returnUrl) {
                localStorage.setItem('logoutReturnUrl', returnUrl);
            }

            // Redirect to login as fallback
            const fallbackUrl = this._configuration.loginUrl || window.location.origin;
            window.location.replace(fallbackUrl);
        }
    }

    clearCookies() {
        try {
            const cookiesToClear = ['idToken', 'accessToken', 'refreshToken', 'orgId', 'uiAnalytics', 'orgList'];
            
            cookiesToClear.forEach(cookieName => {
                // Clear with domain
                document.cookie = `${cookieName}=; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                // Clear without domain (fallback)
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                // Clear with root path
                document.cookie = `${cookieName}=; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                // Clear with current path
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${window.location.pathname};`;
            });
            
            // Also clear any potential Cognito-related cookies
            const cognitoCookies = ['CognitoIdentityServiceProvider', 'amplify-signin-with-hostedUI'];
            cognitoCookies.forEach(cookieName => {
                document.cookie = `${cookieName}=; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            });
            
            console.log('Cookies cleared successfully');
        } catch (error) {
            console.warn('Error clearing cookies:', error);
        }
    }

    setCookies(e) {
        const idTokenExp = this.getTokenSessionTime(e.detail.idToken) || defaultSessionExpTime;
        const authTokenExp = this.getTokenSessionTime(e.detail.accessToken) || defaultSessionExpTime;
        document.cookie = `idToken=${e.detail.idToken}; domain=${domain}; max-age=${idTokenExp}; path=/; Secure; SameSite=Lax`;
        document.cookie = `accessToken=${e.detail.accessToken}; domain=${domain}; max-age=${authTokenExp}; path=/; Secure; SameSite=Lax`;
        document.cookie = `refreshToken=${e.detail.refreshToken}; domain=${domain}; max-age=${defaultSessionExpTime}; path=/; Secure; SameSite=Lax`;
        document.cookie = `orgId=${e.detail.selectedOrg}; domain=${domain}; max-age=${idTokenExp}; path=/; Secure; SameSite=Lax`;
        document.cookie = `uiAnalytics=${e.detail.uiAnalytics}; domain=${domain}; max-age=${idTokenExp}; path=/; Secure; SameSite=Lax`;
        document.cookie = `orgList=${e.detail.orgList}; domain=${domain}; max-age=${idTokenExp}; path=/; Secure; SameSite=Lax`;
    }

    getIdTokenFromCookies() {
        return this.getCookie('idToken');
    }

    async checkAuthentication() {
        console.log('Checking authentication...');
        try {
            const user = await getCurrentUser();
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            if (token) {
                this.tokenManagement.addUserIDToken(token);
            }
            return { ...user, signInUserSession: session };
        } catch (e) {
            console.log(e);
        }
    }

    async checkAuthenticationByPassCache() {
        try {
            const user = await getCurrentUser();
            const session = await fetchAuthSession({ forceRefresh: true });
            const attributes = await fetchUserAttributes();
            const token = session.tokens?.idToken?.toString();
            user.attributes = attributes;
            if (token) {
                this.tokenManagement.addUserIDToken(token);
            }
            return { ...user, signInUserSession: session };
        } catch (e) {
            console.log(e);
        }
    }

    async getSession() {
        try {
            return await fetchAuthSession();
        } catch (e) {
            console.log(e);
        }
    }

    async dispatchTokenRefresh(){
        Hub.dispatch('auth', { event: 'tokenRefresh' });
    }

    parseJwt (token) {
        if(!token) return {};
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    getTokenSessionTime(token) {
        const parsedToken = this.parseJwt(token);
        return parsedToken['custom:session_exp'];
    }

    async authorizeByTokens(idToken, accessToken, refreshToken) {
        // create a CognitoAccessToken using the response accessToken
        const IdToken = new AmazonCognitoIdentity.CognitoIdToken({ IdToken: idToken });
        const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({ AccessToken: accessToken });
        const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({RefreshToken: refreshToken });

        const sessionData = {
            IdToken: IdToken,
            AccessToken: AccessToken,
            RefreshToken: RefreshToken,
        };

        const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

        let poolData = {
            UserPoolId: this._configuration.userPoolId,
            ClientId: this._configuration.userPoolWebClientId,
        };
        
        // pass the poolData object to CognitoUserPool 
        let userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
        
        // create an object containing the username and user pool. 
        // You can get the username from CognitoAccessToken object 
        // we created above.
        let userData = {
            Username: AccessToken.payload.username, 
            Pool: userPool
        };
        
        // create a cognito user using the userData object 
        let cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
        
        // set the cognito user session w/ the CognitoUserSession
        cognitoUser.setSignInUserSession(session);
        
        // get the Amplify authenticated user      
        const user = await getCurrentUser();
        const authSession = await fetchAuthSession({ forceRefresh: true });
        return { ...user, signInUserSession: authSession };
    }

    saveRememberMeToLs(rememberMe) {
        window.localStorage.setItem('rememberMe', rememberMe);
    }
    saveUIAnalyticsToLs(uiAnalytics) {
        window.localStorage.setItem('uiAnalytics', uiAnalytics);
    }
    getUIAnalytics() {
        window.localStorage.getItem('uiAnalytics');
    }

    async initiatePasswordReset(username) {
        return resetPassword({ username });
    }

    async completePasswordReset(username, code, newPassword) {
        return confirmResetPassword({ username, confirmationCode: code, newPassword });
    }

    async changePassword(user, newPassword) {
        return confirmSignIn({ challengeResponse: newPassword });       
    }

    async renewSession() {
        try {
          await getCurrentUser();
          const currentSession = await fetchAuthSession({ forceRefresh: true });
          return currentSession;
        } catch (error) {
          console.error('Error refreshing session:', error);
          throw error;
        }
      }
    getCookie(name) {
        const value = `; ${document.cookie}`;

        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts?.pop()?.split(';').shift();
        }

        return null;
    }

}

export default CognitoService;
